import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, FileText, Link as LinkIcon, Eye, Upload, Image, File, Trash2, X, ChevronRight, Plus, Check } from 'lucide-react';
import Header from '../components/Layout/Header';
import Modal from '../components/Modal';
import { patients } from '../data/mockData';

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 80px;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 0;
`;

const ProfileHeader = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 24px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const PatientImageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const PatientImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const OnlineIndicator = styled.div`
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.successText};
  border: 3px solid ${({ theme }) => theme.colors.white};
`;

const PatientBasicInfo = styled.div`
  flex: 1;
`;

const PatientName = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
`;

const PatientId = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 4px 0;
`;

const PatientBirthDate = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 8px 0;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  background: ${({ theme }) => theme.colors.info};
  color: ${({ theme }) => theme.colors.infoText};
`;

const Section = styled.section`
  background: ${({ theme }) => theme.colors.white};
  padding: 24px 20px;
  margin-bottom: 8px;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  background: ${({ theme }) => theme.colors.gray};
  color: ${({ theme }) => theme.colors.text};

  &:disabled {
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  background: ${({ theme }) => theme.colors.gray};
  color: ${({ theme }) => theme.colors.text};

  &:disabled {
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  background: ${({ theme }) => theme.colors.gray};
  color: ${({ theme }) => theme.colors.text};
  min-height: 80px;
  resize: vertical;
  font-family: ${({ theme }) => theme.fonts.primary};

  &:disabled {
    cursor: not-allowed;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;

  input {
    cursor: pointer;
  }
`;

const AppointmentItem = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
    margin: 0 -20px;
    padding: 16px 20px;
  }
`;

const AppointmentContent = styled.div`
  flex: 1;
`;

const AppointmentDate = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 4px;

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const AppointmentType = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

const AppointmentDoctor = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FormsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const FormCard = styled.button`
  background: ${({ theme }) => theme.colors.gray};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 20px 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;

  svg {
    width: 32px;
    height: 32px;
    color: ${({ theme }) => theme.colors.primary};
  }

  span {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ theme }) => theme.colors.text};
    text-align: center;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.info};
  }
`;

// Nuevos styled components para formularios clínicos
const FormsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const AddFormButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FormulariosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FormularioItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.border};
  }
`;

const FormularioIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ $completed, theme }) => $completed ? `${theme.colors.success}20` : `${theme.colors.primary}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ $completed, theme }) => $completed ? theme.colors.successText : theme.colors.primary};
  }
`;

const FormularioInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FormularioName = styled.span`
  display: block;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

const FormularioMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FormularioStatus = styled.span`
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  background: ${({ $completed, theme }) => $completed ? `${theme.colors.success}20` : `${theme.colors.warning}20`};
  color: ${({ $completed, theme }) => $completed ? theme.colors.successText : theme.colors.warningText};
`;

const EmptyFormularios = styled.div`
  text-align: center;
  padding: 32px 20px;
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 40px;
    height: 40px;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.gray};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 8px;
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const DocumentDetails = styled.div`
  flex: 1;
`;

const DocumentName = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

const DocumentDate = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ViewButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  padding: 4px 8px;
  transition: all 0.3s ease;

  &:hover {
    text-decoration: underline;
  }
`;

const UploadSection = styled.div`
  margin-bottom: 20px;
`;

const UploadArea = styled.div`
  border: 2px dashed ${({ theme, $isDragging }) => $isDragging ? theme.colors.primary : theme.colors.border};
  border-radius: 12px;
  padding: 30px 20px;
  text-align: center;
  background: ${({ theme, $isDragging }) => $isDragging ? theme.colors.info : theme.colors.gray};
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.info};
  }
`;

const UploadIcon = styled.div`
  margin-bottom: 12px;

  svg {
    width: 40px;
    height: 40px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const UploadText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const UploadSubtext = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 16px;
`;

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text};

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const DocumentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DeleteDocButton = styled.button`
  background: ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.dangerText};
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    opacity: 0.9;
  }
`;

const FileTypeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $isImage, theme }) => $isImage ? theme.colors.info : theme.colors.warning};

  svg {
    width: 20px;
    height: 20px;
    color: ${({ $isImage, theme }) => $isImage ? theme.colors.primary : theme.colors.warningText};
  }
`;

const PerfilPaciente = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const patient = patients.find(p => p.id === parseInt(id)) || patients[0];
  
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [documents, setDocuments] = useState(patient.documents || []);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  // Estado para formularios clínicos del paciente
  const [formulariosLlenados, setFormulariosLlenados] = useState([
    { id: 1, formId: 1, name: 'Consentimiento Informado', completedAt: '2025-01-20', completed: true },
    { id: 2, formId: 2, name: 'Historia Clínica Inicial', completedAt: '2025-01-20', completed: true },
  ]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const today = new Date().toISOString().split('T')[0];
    const newDocs = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      date: today,
      type: file.type,
      size: (file.size / 1024).toFixed(1) + ' KB',
      isImage: file.type.startsWith('image/')
    }));
    setDocuments(prev => [...newDocs, ...prev]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFiles(files);
    }
    e.target.value = '';
  };

  const handleDeleteDocument = (docId) => {
    setDocumentToDelete(docId);
    setShowDeleteModal(true);
  };

  const confirmDeleteDocument = () => {
    setDocuments(prev => prev.filter(d => d.id !== documentToDelete));
    setShowDeleteModal(false);
    setDocumentToDelete(null);
  };

  const isImageFile = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
  };

  return (
    <PageContainer>
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteDocument}
        title="Eliminar Documento"
        message="¿Estás seguro de eliminar este documento? Esta acción no se puede deshacer."
        type="warning"
        confirmText="Eliminar"
        cancelText="Cancelar"
        showCancel
      />

      <Header title="Perfil del Paciente" showBack />
      
      <Content>
        <ProfileHeader>
          <PatientImageContainer>
            <PatientImage>
              <img src={patient.image} alt={patient.name} />
              {patient.status === 'Activo' && <OnlineIndicator />}
            </PatientImage>
            <PatientBasicInfo>
              <PatientName>{patient.name}</PatientName>
              <PatientId>ID: {patient.patientId}</PatientId>
              <PatientBirthDate>F. Nacimiento: {patient.birthDate}</PatientBirthDate>
              <StatusBadge>{patient.status}</StatusBadge>
            </PatientBasicInfo>
          </PatientImageContainer>
        </ProfileHeader>

        <Section>
          <SectionTitle>Datos Personales</SectionTitle>
          <FormGrid>
            <FormField>
              <Label>Nombre</Label>
              <Input type="text" value={patient.firstName} disabled />
            </FormField>
            <FormField>
              <Label>Apellido</Label>
              <Input type="text" value={patient.lastName} disabled />
            </FormField>
            <FormField>
              <Label>DNI / ID</Label>
              <Input type="text" value={patient.dni} disabled />
            </FormField>
            <FormField>
              <Label>Fecha de Nacimiento</Label>
              <Input type="text" value={patient.birthDate} disabled />
            </FormField>
            <FormField>
              <Label>Género</Label>
              <Select disabled>
                <option>{patient.gender}</option>
              </Select>
            </FormField>
            <FormField>
              <Label>Teléfono</Label>
              <Input type="tel" value={patient.phone} disabled />
            </FormField>
            <FormField className="full-width">
              <Label>Email</Label>
              <Input type="email" value={patient.email} disabled />
            </FormField>
          </FormGrid>

          <FormField>
            <Label>Tipo de Paciente</Label>
            <RadioGroup>
              <RadioLabel>
                <input type="radio" checked={patient.type === 'Adulto'} disabled />
                Adulto
              </RadioLabel>
              <RadioLabel>
                <input type="radio" checked={patient.type === 'Pediátrico'} disabled />
                Pediátrico
              </RadioLabel>
            </RadioGroup>
          </FormField>

          <FormField style={{ marginTop: '16px' }}>
            <Label>Historial de Alergias</Label>
            <TextArea value={patient.allergies} disabled />
          </FormField>
        </Section>

        <Section>
          <SectionTitle>Historial de Citas</SectionTitle>
          {patient.appointments && patient.appointments.length > 0 ? (
            patient.appointments.map((appointment, index) => (
              <AppointmentItem key={index} onClick={() => navigate(`/detalle-cita/${index + 1}`)}>
                <AppointmentContent>
                  <AppointmentDate>
                    <Calendar />
                    {appointment.date}
                  </AppointmentDate>
                  <AppointmentType>{appointment.type}</AppointmentType>
                  <AppointmentDoctor>{appointment.doctor}</AppointmentDoctor>
                </AppointmentContent>
                <ChevronRight size={20} color="#6C757D" />
              </AppointmentItem>
            ))
          ) : (
            <p style={{ color: '#6C757D', fontSize: '14px' }}>No hay citas registradas.</p>
          )}
        </Section>

        <Section>
          <FormsSectionHeader>
            <SectionTitle style={{ margin: 0 }}>Formularios Clínicos</SectionTitle>
            <AddFormButton onClick={() => navigate(`/seleccionar-formulario/${id}`, {
              state: { returnTo: `/perfil-paciente/${id}` }
            })}>
              <Plus />
              Agregar
            </AddFormButton>
          </FormsSectionHeader>

          {formulariosLlenados.length > 0 ? (
            <FormulariosList>
              {formulariosLlenados.map(form => (
                <FormularioItem 
                  key={form.id}
                  onClick={() => {
                    if (form.completed) {
                      // Si está completado, abrir vista previa
                      navigate(`/ver-formulario/${form.formId}/${id}`, {
                        state: { 
                          returnTo: `/perfil-paciente/${id}`,
                          completedAt: form.completedAt
                        }
                      });
                    } else {
                      // Si no está completado, abrir para llenar
                      navigate(`/llenar-formulario/${form.formId}/${id}`, {
                        state: { returnTo: `/perfil-paciente/${id}` }
                      });
                    }
                  }}
                >
                  <FormularioIcon $completed={form.completed}>
                    {form.completed ? <Check /> : <FileText />}
                  </FormularioIcon>
                  <FormularioInfo>
                    <FormularioName>{form.name}</FormularioName>
                    <FormularioMeta>
                      {form.completed ? `Completado: ${form.completedAt}` : 'Pendiente de completar'}
                    </FormularioMeta>
                  </FormularioInfo>
                  <FormularioStatus $completed={form.completed}>
                    {form.completed ? 'Completado' : 'Pendiente'}
                  </FormularioStatus>
                  <ChevronRight size={18} color="#6C757D" />
                </FormularioItem>
              ))}
            </FormulariosList>
          ) : (
            <EmptyFormularios>
              <FileText />
              <p>No hay formularios clínicos para este paciente</p>
            </EmptyFormularios>
          )}
        </Section>

        <Section>
          <SectionTitle>Adjuntos</SectionTitle>
          
          <UploadSection>
            <UploadArea
              $isDragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon>
                <Upload />
              </UploadIcon>
              <UploadText>Arrastra archivos aquí o haz clic para seleccionar</UploadText>
              <UploadSubtext>Soporta imágenes (JPG, PNG) y documentos (PDF, DOC)</UploadSubtext>
              
              <UploadButtons onClick={(e) => e.stopPropagation()}>
                <UploadButton onClick={() => imageInputRef.current?.click()}>
                  <Image />
                  Subir Foto
                </UploadButton>
                <UploadButton onClick={() => fileInputRef.current?.click()}>
                  <File />
                  Subir Documento
                </UploadButton>
              </UploadButtons>
            </UploadArea>
            
            <HiddenInput
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
              multiple
            />
            <HiddenInput
              type="file"
              ref={imageInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              multiple
            />
          </UploadSection>

          {documents.length > 0 ? (
            documents.map((doc) => (
              <DocumentItem key={doc.id || doc.name}>
                <DocumentInfo>
                  <FileTypeIcon $isImage={doc.isImage || isImageFile(doc.name)}>
                    {doc.isImage || isImageFile(doc.name) ? <Image /> : <File />}
                  </FileTypeIcon>
                  <DocumentDetails>
                    <DocumentName>{doc.name}</DocumentName>
                    <DocumentDate>
                      Subido: {doc.date} {doc.size && `• ${doc.size}`}
                    </DocumentDate>
                  </DocumentDetails>
                </DocumentInfo>
                <DocumentActions>
                  <ViewButton>Ver</ViewButton>
                  <DeleteDocButton onClick={() => handleDeleteDocument(doc.id)}>
                    <Trash2 />
                  </DeleteDocButton>
                </DocumentActions>
              </DocumentItem>
            ))
          ) : (
            <p style={{ color: '#6C757D', fontSize: '14px' }}>No hay documentos adjuntos.</p>
          )}
        </Section>
      </Content>
    </PageContainer>
  );
};

export default PerfilPaciente;
